import { IInputType } from "@Components/shared/EditableCell";
import { camelToTitle, DateTime } from "@Utils";
import { FormItemProps } from "antd/lib";
import { ColumnsType, ColumnProps } from "antd/lib/table";
import { ColumnTitle } from "antd/lib/table/interface";
import { ReactNode } from "react";

/**
 * Custom render function type for table columns.
 * @param datum - The value of the cell.
 * @param record - The record of the row.
 * @param index - The index of the row.
 *
 * @example
 * const customRender: ICustomRenderFunction<User> = (datum, record, index) => {
 *   return `${record.firstName} ${record.lastName}`;
 * };
 */
type ICustomRenderFunction<T> = (datum: unknown, record: T, index: number) => string | number | object;

/**
 * Render props type for table columns.
 */
type IRenderProps<T> = "date" | "yesNo" | "yesOptional" | ICustomRenderFunction<T>;


/**
 * Interface for column data configuration.
 */
export interface IColumnData<T> extends Omit<ColumnProps<T>, "key" | "render">, Omit<FormItemProps<T>, "children"> {
    inputType?: IInputType;
    key: keyof T | "actions" | "add" | "delete" | "download" | "select";
    render?: IRenderProps<T>;
    title?: ColumnTitle<ReactNode>;
    props?: Record<string, unknown>;
}

/**
 * Formats a date string to a readable format.
 * @param rawDate - The raw date string.
 *
 * @example
 * const formattedDate = dateFormatter("2023-06-10");
 * console.log(formattedDate); // Output: "Jun 10th 2023"
 */
const dateFormatter = (rawDate: string): string => {
  if (DateTime(rawDate).isBlankDate()) return "";
  return DateTime(rawDate).format("MMM Do YYYY");
};

/**
 * Converts a boolean or number value to a string representation.
 * Used by renderFn
 * @param datum - The boolean or number value.
 * @param yes - The string representation for a true or 1 value (default: "Yes").
 * @param no - The string representation for a false or 0 value (default: "No").
 *
 * @example
 * const result1 = yesNo(true);
 * console.log(result1); // Output: "Yes"
 *
 * const result2 = yesNo(0, "Enabled", "Disabled");
 * console.log(result2); // Output: "Disabled"
 */
const yesNo = (datum: boolean | number, yes = "Yes", no = "No") => (datum === 1 || datum === true ? yes : no);

/**
 * Maps column data to Ant Design table columns.
 * @param columnData - The column data configuration array.
 *
 * @example
 * const columnData: IColumnData<User>[] = [
 *   { key: "firstName", title: "First Name" },
 *   { key: "lastName", title: "Last Name" },
 *   { key: "age", render: "date" },
 * ];
 * const columns = columnMapper<User>(columnData);
 */
export function columnMapper<T>(columnData: IColumnData<T>[]): ColumnsType<T> {
  return columnData.map(({ render, ...datum }) => ({
    ...datum,
    key: datum.key.toString(),
    title: !!datum.title || typeof datum.title === "string" ? datum.title : formatColumnTitle(datum.key.toString()),
    // either the render or the dataIndex
    ...renderFn<T>(datum, render),
  }));
}

/**
 * Formats a column key to a readable title.
 * Used by columnMapper to generate column titles.
 * @param key - The column key.
 *
 * @example
 * const title = formatColumnTitle("firstName");
 * console.log(title); // Output: "First Name"
 */
const formatColumnTitle = (key: string): string => {
  let result = camelToTitle(key);
  if (result === "Users") result = "User";
  return result;
};

/**
 * Generates the render function for a column based on the render prop.
 * @param datum - The column data configuration.
 * @param render - The render prop.
 */
function renderFn<T>(datum: IColumnData<T>, render: IRenderProps<T>) {
  if (!render) return { dataIndex: datum.key.toString() };

  if (render === "date") return { render: (_: unknown, record: T) => dateFormatter(record[datum.key.toString()]) };
  if (render === "yesNo") return { render: (_: unknown, record: T) => yesNo(record[datum.key.toString()]) };
  if (render === "yesOptional")
    return { render: (_: unknown, record: T) => yesNo(record[datum.key.toString()], undefined, "Optional") };

  return {
    render: (_: unknown, record: T, index: number) =>
      typeof render !== "string" && render(record[datum.key.toString()], record, index),
  };
}

/**
 * Filters a specified quantity of objects from an array based on a key-value condition.
 * @param list - The array of objects.
 * @param key - The key to compare the value against.
 * @param value - The value to match.
 * @param quantity - The maximum number of objects to return.
 *
 * @example
 * const users = [
 *   { id: 1, name: "John", age: 25 },
 *   { id: 2, name: "Jane", age: 30 },
 *   { id: 3, name: "Bob", age: 25 },
 * ];
 * const filteredUsers = filterXOfObjectsFromArrayByKey(users, "age", 25, 1);
 * console.log(filteredUsers); // Output: [{ id: 1, name: "John", age: 25 }]
 */
export function filterXOfObjectsFromArrayByKey<T extends Record<keyof T, unknown>>(
  list: T[],
  key: keyof T,
  value: unknown,
  quantity: number
): T[] {
  const items = list.filter((item) => item[key] === value);
  return items.slice(0, quantity);
}

/**
 * Filters a specified quantity of objects from an array based on a custom filter function.
 * @param list - The array of objects.
 * @param filter - The filter function to apply to each object.
 * @param quantity - The maximum number of objects to return.
 *
 * @example
 * const users = [
 *   { id: 1, name: "John", age: 25 },
 *   { id: 2, name: "Jane", age: 30 },
 *   { id: 3, name: "Bob", age: 35 },
 * ];
 * const filteredUsers = filterXOfObjectsFromArray(users, (user) => user.age > 30, 1);
 * console.log(filteredUsers); // Output: [{ id: 3, name: "Bob", age: 35 }]
 */
export function filterXOfObjectsFromArray<T extends Record<keyof T, unknown>>(
  list: T[],
  filter: (item: T) => boolean,
  quantity: number
): T[] {
  const items = list.filter(filter);
  return items.slice(0, quantity);
}


/**
 * A component type with props and children props.
 */
interface ComponentType<P = Record<string, unknown>> {
  props: P;
  children: { props: P };
  render(): JSX.Element;
}

/**
 * Extends the props of a component by adding a new prop.
 * @param component - The component to extend.
 * @param prop - The name of the new prop.
 * @param value - The value of the new prop.
 *
 * @note This function is not used currently
 *
 * @example
 * const MyComponent: ComponentType<{ name: string }> = {
 *   props: { name: "John" },
 *   children: { props: { name: "John" } },
 *   render() {
 *     return <div>Hello, {this.props.name}!</div>;
 *   },
 * };
 * const ExtendedComponent = extendProps(MyComponent, "age", 25);
 * console.log(ExtendedComponent.props); // Output: { name: "John", age: 25 }
 */
export function extendProps<T extends ComponentType<unknown>>(component: T, prop: string, value: unknown): T {
  // if object is not extensible return original component
  if (!Object.isExtensible(component)) return component;

  // Get the current props for the component
  const props = component?.props || component?.children?.props;

  // if not props return original component
  if (!props) return component;

  // Add the new prop to the props object
  props[prop] = value;

  // Return the updated component
  return component;
}

/**
 * Removes a key-value pair from a Map and returns the removed value.
 * If the key does not exist in the Map, it returns undefined.
 *
 * @param {Map<K, V>} map - The Map object to remove the entry from.
 * @param {K} key - The key to remove from the Map.
 *
 * @example
 * const myMap = new Map<string, number>([
 *   ['a', 1],
 *   ['b', 2],
 *   ['c', 3]
 * ]);
 *
 * const removedValue = removeAndReturn(myMap, 'b');
 * console.log(removedValue); // Output: 2
 * console.log(myMap); // Output: Map { 'a' => 1, 'c' => 3 }
 */
export function removeAndReturn<K, V>(map: Map<K, V>, key: K): V | undefined {
  const value = map.get(key);
  map.delete(key);
  return value;
}